import {createContext, useContext, useState, useEffect} from 'react';
import {FlexBuilderType} from '../constant';

type PartialAtLeastOne<T, Keys extends keyof T = keyof T> = Partial<T> & { [K in Keys]: Required<Pick<T, K>> }[Keys];

interface State {
    isPublishing: boolean;
    view: 'code' | 'preview' | 'doc';
    isEditingInCodeView: boolean;
    editCode: string; // 全屏编辑时存储的代码
    isArtifactLoading: boolean;
    selectArtifactId: string;
    publishArtifactId: string;
    isPublishArtifactModified: boolean;
    editorRef: React.MutableRefObject<any>;
    preventTriggerChangeRef: React.MutableRefObject<boolean>;
    chatMessageLength: null | number;
    docStatusOfPreviewTab: 'edit' | 'read';
    endpointOfPreviewTab: 'pc' | 'mobile';
    previewTabPopperVisible: boolean;
    flexBuilderType?: FlexBuilderType;
}

interface Actions {
    setPublishing: (isPublishing: boolean) => void;
    setView: (view: 'code' | 'preview' | 'doc') => void;
    setIsEditingInCodeView: (isEditing: boolean) => void;
    setEditCode: (code: string, isNeedUpdateEditorCode?: boolean) => void;
    setIsArtifactLoading: (isLoading: boolean) => void;
    setSelectArtifactId: (id: string) => void;
    setPublishArtifactId: (id: string) => void;
    setIsPublishArtifactModified: (isModified: boolean) => void;
    setEditorRef: (editor: any) => void;
    clearUndoStack: () => void;
    setPreventTriggerChangeRef: (val: boolean) => void;
    setChatMessageLength: (length: null|number) => void;
    setDocStatusOfPreviewTab: (value: State['docStatusOfPreviewTab']) => void;
    setEndpointOfPreviewTab: (value: State['endpointOfPreviewTab']) => void;
    setPreviewTabPopperVisible: (visible: boolean) => void;
    setFlexBuilderType: (type: FlexBuilderType) => void;
}

type Store = State & Actions;

// === 单例状态容器 ===
let state: State = {
    isPublishing: false,
    view: 'preview',
    isEditingInCodeView: false,
    editCode: '',
    isArtifactLoading: false,
    selectArtifactId: '',
    publishArtifactId: '',
    isPublishArtifactModified: false,
    editorRef: {current: null},
    preventTriggerChangeRef: {current: false},
    chatMessageLength: null,
    docStatusOfPreviewTab: 'edit',
    endpointOfPreviewTab: 'pc',
    previewTabPopperVisible: false,
    flexBuilderType: FlexBuilderType.frontend,
};

const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

const getSnapshot = () => state;

const setState = (partial: Partial<State>) => {
    state = {...state, ...partial};
    listeners.forEach((listener) => listener());
};

export const store: Store = {
    get isPublishing() {
        return state.isPublishing;
    },
    get view() {
        return state.view;
    },
    get isEditingInCodeView() {
        return state.isEditingInCodeView;
    },
    get editCode() {
        return state.editCode;
    },
    get isArtifactLoading() {
        return state.isArtifactLoading;
    },
    get selectArtifactId() {
        return state.selectArtifactId;
    },
    get publishArtifactId() {
        return state.publishArtifactId;
    },
    get isPublishArtifactModified() {
        return state.isPublishArtifactModified;
    },
    get editorRef() {
        return state.editorRef;
    },
    get preventTriggerChangeRef() {
        return state.preventTriggerChangeRef;
    },
    get chatMessageLength() {
        return state.chatMessageLength;
    },
    get docStatusOfPreviewTab() {
        return state.docStatusOfPreviewTab;
    },
    get endpointOfPreviewTab() {
        return state.endpointOfPreviewTab;
    },
    get previewTabPopperVisible() {
        return state.previewTabPopperVisible;
    },
    get flexBuilderType() {
        return state.flexBuilderType;
    },
    setPublishing: (isPublishing) => {
        setState({isPublishing});
    },
    setView: (view) => {
        setState({view});
    },
    setIsEditingInCodeView: (isEditing) => {
        setState({isEditingInCodeView: isEditing});
    },
    clearUndoStack: () => {
        const editor = store.editorRef.current;
        const model = editor?.getModel();
        if (model) {
            const currentValue = model.getValue();
            // 设置setPreventTriggerChangeRef是为了防止本次setValue操作会触发编辑态的onChange回调，从而导致调用接口保存当前版本的改动
            store.setPreventTriggerChangeRef(true);
            // 重置内容（会清空撤销栈）
            model.setValue(currentValue);
            store.setPreventTriggerChangeRef(false);
        }
    },
    setEditCode: (code, isNeedUpdateEditorCode = true) => {
        setState({editCode: code});

        if (isNeedUpdateEditorCode) {
            const editor = store.editorRef.current;
            const model = editor?.getModel();

            if (model) {
                store.setPreventTriggerChangeRef(true);
                model.setValue(code);
                store.setPreventTriggerChangeRef(false);
            }
        }
    },
    setIsArtifactLoading: (isLoading) => {
        setState({isArtifactLoading: isLoading});
    },
    setSelectArtifactId: (id) => {
        setState({selectArtifactId: id});
    },
    setPublishArtifactId: (id) => {
        setState({publishArtifactId: id});
    },
    setIsPublishArtifactModified: (isModified) => {
        setState({isPublishArtifactModified: isModified});
    },
    setEditorRef: (editor: any) => {
        state.editorRef.current = editor;
    },
    setPreventTriggerChangeRef: (val: boolean) => {
        state.preventTriggerChangeRef.current = val;
    },
    setChatMessageLength: (val: number|null) => {
        state.chatMessageLength = val;
    },
    setDocStatusOfPreviewTab: (value: State['docStatusOfPreviewTab']) => {
        setState({docStatusOfPreviewTab: value});
    },
    setEndpointOfPreviewTab: (value: State['endpointOfPreviewTab']) => {
        setState({endpointOfPreviewTab: value});
    },
    setPreviewTabPopperVisible: (visible: boolean) => {
        setState({previewTabPopperVisible: visible});
    },
    setFlexBuilderType: (type: FlexBuilderType) => {
        state.flexBuilderType = type;
    },
};

// eslint-disable-next-line no-underscore-dangle
(window as any).__DEV_FLEX_BUILDER_STORE__ = store;

// === Context ===
const StoreContext = createContext<Store | null>(null);

export const StoreProvider: React.FC<React.PropsWithChildren<unknown>> = ({children}) => {
    const [snapshot, setSnapshot] = useState(getSnapshot);

    useEffect(() => {
        const unsubscribe = subscribe(() => {
            setSnapshot(getSnapshot());
        });
        return () => {
            // 组件销毁时，初始化状态
            setState({
                isPublishing: false,
                view: 'preview',
                editCode: '',
                isArtifactLoading: false,
                selectArtifactId: '',
                publishArtifactId: '',
                isPublishArtifactModified: false,
                isEditingInCodeView: false,
                chatMessageLength: null,
                docStatusOfPreviewTab: 'edit',
                endpointOfPreviewTab: 'pc',
                previewTabPopperVisible: false,
                flexBuilderType: undefined,
            });
            unsubscribe();
        };
    }, []);

    return (
        <StoreContext.Provider value={{...snapshot, ...store}}>
            {children}
        </StoreContext.Provider>
    );
};

export const useStore = () => {
    const ctx = useContext(StoreContext);
    if (!ctx) {
        throw new Error('useStore must be used inside StoreProvider');
    }
    return ctx;
};
